/**
 * Record Tracker Utility
 * Tracks modifications to records and provides change indicators
 */

import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Get the last modification time for a record
 * @param {string} resourceId - The ID of the record
 * @param {string} resourceType - The type of record (e.g., 'sales')
 * @returns {Promise<Date|null>} Last modification timestamp or null if not found
 */
export const getLastModified = async (resourceId, resourceType = 'sales') => {
  try {
    const logsRef = collection(db, 'audit_logs');
    const q = query(
      logsRef,
      where('resourceId', '==', resourceId),
      where('resource', '==', resourceType),
      where('action', 'in', ['CREATE', 'UPDATE']),
      orderBy('timestamp', 'desc'),
      limit(1)
    );
    
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const log = snapshot.docs[0].data();
      return log.timestamp.toDate();
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching last modified date:', error);
    return null;
  }
};

/**
 * Check if a record has been modified recently
 * @param {string} resourceId - The ID of the record
 * @param {number} hoursThreshold - Number of hours to consider "recent" (default: 24)
 * @param {string} resourceType - The type of record (e.g., 'sales')
 * @returns {Promise<boolean>} True if record was modified recently
 */
export const isRecentlyModified = async (resourceId, hoursThreshold = 24, resourceType = 'sales') => {
  const lastModified = await getLastModified(resourceId, resourceType);
  if (!lastModified) return false;
  
  const now = new Date();
  const diffHours = (now - lastModified) / (1000 * 60 * 60);
  return diffHours <= hoursThreshold;
};

/**
 * Get modification count for a record within a time period
 * @param {string} resourceId - The ID of the record
 * @param {number} daysPeriod - Number of days to look back (default: 7)
 * @param {string} resourceType - The type of record (e.g., 'sales')
 * @returns {Promise<number>} Number of modifications in the period
 */
export const getModificationCount = async (resourceId, daysPeriod = 7, resourceType = 'sales') => {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - daysPeriod);
    
    const logsRef = collection(db, 'audit_logs');
    const q = query(
      logsRef,
      where('resourceId', '==', resourceId),
      where('resource', '==', resourceType),
      where('action', '==', 'UPDATE'),
      where('timestamp', '>=', startDate),
      orderBy('timestamp', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error('Error fetching modification count:', error);
    return 0;
  }
};

/**
 * Batch get last modified times for multiple records
 * @param {Array<string>} resourceIds - Array of record IDs
 * @param {string} resourceType - The type of record (e.g., 'sales')
 * @returns {Promise<Object>} Map of resourceId to last modified date
 */
export const batchGetLastModified = async (resourceIds, resourceType = 'sales') => {
  try {
    const results = {};
    
    // Process in batches to avoid Firestore limits
    const batchSize = 10;
    for (let i = 0; i < resourceIds.length; i += batchSize) {
      const batch = resourceIds.slice(i, i + batchSize);
      
      const logsRef = collection(db, 'audit_logs');
      const q = query(
        logsRef,
        where('resourceId', 'in', batch),
        where('resource', '==', resourceType),
        where('action', 'in', ['CREATE', 'UPDATE']),
        orderBy('timestamp', 'desc')
      );
      
      const snapshot = await getDocs(q);
      
      // Group by resourceId and get the latest timestamp for each
      const batchResults = {};
      snapshot.docs.forEach(doc => {
        const log = doc.data();
        const resourceId = log.resourceId;
        
        if (!batchResults[resourceId] || log.timestamp.toDate() > batchResults[resourceId]) {
          batchResults[resourceId] = log.timestamp.toDate();
        }
      });
      
      Object.assign(results, batchResults);
    }
    
    return results;
  } catch (error) {
    console.error('Error batch fetching last modified dates:', error);
    return {};
  }
};

/**
 * Format a date for display in the UI
 * @param {Date} date - Date to format
 * @param {boolean} includeTime - Whether to include time (default: true)
 * @returns {string} Formatted date string
 */
export const formatLastModified = (date, includeTime = true) => {
  if (!date) return 'Never';
  
  const now = new Date();
  const diffTime = now - date;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffTime / (1000 * 60));
  
  // If less than 1 minute ago
  if (diffMinutes < 1) {
    return 'Just now';
  }
  
  // If less than 1 hour ago
  if (diffHours < 1) {
    return `${diffMinutes} minutes ago`;
  }
  
  // If less than 24 hours ago
  if (diffDays < 1) {
    return `${diffHours} hours ago`;
  }
  
  // If less than 7 days ago
  if (diffDays < 7) {
    return `${diffDays} days ago`;
  }
  
  // Otherwise show the date
  if (includeTime) {
    return date.toLocaleString();
  } else {
    return date.toLocaleDateString();
  }
};

/**
 * Get change indicator style based on modification time
 * @param {Date} lastModified - Last modification date
 * @param {number} recentHours - Hours to consider "recent" (default: 24)
 * @returns {Object} Style object for change indicator
 */
export const getChangeIndicatorStyle = (lastModified, recentHours = 24) => {
  if (!lastModified) return null;
  
  const now = new Date();
  const diffHours = (now - lastModified) / (1000 * 60 * 60);
  
  if (diffHours <= 1) {
    return {
      backgroundColor: '#ef4444', // red-500
      color: 'white',
      fontSize: '10px',
      padding: '2px 6px',
      borderRadius: '10px',
      fontWeight: 'bold',
      animation: 'pulse 2s infinite'
    };
  } else if (diffHours <= 6) {
    return {
      backgroundColor: '#f97316', // orange-500
      color: 'white',
      fontSize: '10px',
      padding: '2px 6px',
      borderRadius: '10px',
      fontWeight: 'bold'
    };
  } else if (diffHours <= recentHours) {
    return {
      backgroundColor: '#eab308', // yellow-500
      color: 'white',
      fontSize: '10px',
      padding: '2px 6px',
      borderRadius: '10px',
      fontWeight: 'bold'
    };
  }
  
  return null;
};

/**
 * Get change indicator text based on modification time
 * @param {Date} lastModified - Last modification date
 * @returns {string|null} Text for change indicator
 */
export const getChangeIndicatorText = (lastModified) => {
  if (!lastModified) return null;
  
  const now = new Date();
  const diffHours = (now - lastModified) / (1000 * 60 * 60);
  
  if (diffHours <= 1) {
    return 'NEW';
  } else if (diffHours <= 6) {
    return 'RECENT';
  } else if (diffHours <= 24) {
    return 'UPDATED';
  }
  
  return null;
};
